import { CornerDownRightIcon, SignatureIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { FormCheckboxFieldDS } from 'components/reusable/form/FormFields'
import {
  Banner,
  Button,
  ButtonGroup,
  CheckboxGroup,
  TextButton,
  TextLink,
  useBreakpoint,
} from 'slab'
import { usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsFinalizeEnrollments as useFinalizeEnrollments } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import { useOnboarding } from '../OnboardingContext'
import {
  type RenewalConfirmEnrollmentFormData,
  type StepComponentProps,
} from '../types'
import { handleMemberValidationErrors } from './ConfirmAndEnrollPage'
import {
  ArbitrationAgreementCard,
  ContractAgreementCard,
} from '../components/ConfirmEnrollmentAgreementCard'
import { CoveredParticipantsComparisonTable } from '../components/CoveredParticipantsComparisonTable/CoveredParticipantsComparisonTable'
import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { SelectedPlansComparisonTable } from '../components/SelectedPlansComparisonTable/SelectedPlansComparisonTable'
import { clearOnboardingStorage } from '../navigationUtils'

export function RenewalConfirmAndEnrollPage ({
  navigateToPreviousStep,
  navigateToSpecificStep,
}: StepComponentProps) {
  const { assign } = useWindowLocation()

  const {
    onboardingData,
    enrollmentEventId,
    updateMemberInfo,
    planChangeSummary,
  } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()
  const [submitError, setSubmitError] = useState<React.ReactNode | null>(null)

  const needContractAgreement =
    onboardingData?.missing_fields?.enrollment?.includes('carrier_contract')
  const needArbitrationAgreement =
    onboardingData?.missing_fields?.enrollment?.includes('carrier_arbitration')
  const needUserPlatformAgreement =
    onboardingData?.missing_fields?.enrollment?.includes(
      'user_platform_agreement'
    )
  const showThatchbotOptInCheckbox =
    onboardingData?.member?.sms_ai_opt_in !== true ||
    onboardingData?.member?.sms_account_notification_opt_in !== true

  const selectedPlanIds = onboardingData?.enrollments?.map((e) => e.id) || []

  const finalizeEnrollments = useFinalizeEnrollments()

  const isAnyMutationInProgress =
    updateMemberInfo.isPending || finalizeEnrollments.isPending

  const defaultValues = {
    member: {
      sms_opt_in:
        onboardingData?.member?.sms_ai_opt_in &&
        onboardingData?.member?.sms_account_notification_opt_in
          ? true
          : null,
    },
    arbitration_agreement: needArbitrationAgreement
      ? {
          self_signature: '',
        }
      : undefined,
    contract_agreement: needContractAgreement
      ? {
          self_signature: '',
        }
      : undefined,
    user_platform_agreement: needUserPlatformAgreement ? false : undefined,
  }

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors: _clearErrors,
    formState: { errors, isValid },
  } = useForm<RenewalConfirmEnrollmentFormData>({
    defaultValues,
    mode: 'onTouched',
  })

  // This hook is needed in case the data refreshes.
  useEffect(() => {
    reset(defaultValues)
  }, [onboardingData])

  const onSubmit = async (formData: RenewalConfirmEnrollmentFormData) => {
    const memberData = {
      sms_ai_opt_in: formData.member.sms_opt_in,
      sms_account_notification_opt_in: formData.member.sms_opt_in,
    }

    updateMemberInfo.mutate(
      {
        member: memberData,
        signatory: {
          arbitration_agreement: formData.arbitration_agreement?.self_signature,
          contract_agreement: formData.contract_agreement?.self_signature,
          user_platform_agreement:
            formData.user_platform_agreement === true ? true : undefined,
        },
      },
      {
        onSuccess: () => {
          finalizeEnrollments.mutate(
            {
              enrollmentEventId,
              data: {
                enrollment_or_selected_plan_ids: selectedPlanIds,
              },
            },
            {
              onSuccess: () => {
                clearOnboardingStorage(enrollmentEventId)
                assign('/member_onboarding/post_enrollment')
              },
              onError: (error) => {
                const errors = handleMemberValidationErrors(
                  error as any,
                  setError
                )
                if (errors.length > 0) {
                  setSubmitError(
                    <OnboardingGenericErrorMessage errors={errors} />
                  )
                }
              },
            }
          )
        },
        onError: (error) => {
          const errors = handleMemberValidationErrors(error as any, setError)
          if (errors.length > 0) {
            setSubmitError(<OnboardingGenericErrorMessage errors={errors} />)
          }
        },
      }
    )
  }

  const selfName = (onboardingData?.member?.participant_name || '')
  const isAgreementValid = (type: 'arbitration' | 'contract') => {
    const agreementValues = watch(`${type}_agreement`)

    // Member signature is always required
    const memberSignatureValid =
      !errors[`${type}_agreement`]?.self_signature &&
      agreementValues?.self_signature !== '' &&
      agreementValues?.self_signature.trim().toLocaleLowerCase() ===
        selfName.trim().toLocaleLowerCase()

    return memberSignatureValid
  }

  const generalMedicalEnrollment = onboardingData?.enrollments?.find((e) => {
    return e.covers_medical === true && e.payment_method === 'paid_by_thatch'
  })
  const carrierName = generalMedicalEnrollment?.carrier_name

  // Buttons
  function renderButtons () {
    const declineButton = (
      <TextButton
        onClick={() => {
          navigateToSpecificStep?.('opted_out')
        }}
        disabled={isAnyMutationInProgress}
      >
        Decline coverage
      </TextButton>
    )

    const backButton = (
      <Button
        variant='secondary'
        onClick={() => {
          navigateToPreviousStep?.(onboardingData)
        }}
        disabled={isAnyMutationInProgress}
      >
        Back
      </Button>
    )

    const renewButton = (
      <Button
        rightIcon={CornerDownRightIcon}
        variant='primary'
        type='submit'
        disabled={
          isAnyMutationInProgress || !isValid || selectedPlanIds.length < 1
        }
      >
        Renew
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <div className='flex justify-between items-center'>
          {declineButton}
          <ButtonGroup size='large'>
            {backButton}
            {renewButton}
          </ButtonGroup>
        </div>
      )
    }

    return (
      <div className='flex flex-col gap-4'>
        <ButtonGroup size='large' layout='vertical'>
          {renewButton}
          {backButton}
        </ButtonGroup>
        {declineButton}
      </div>
    )
  }

  // Render agreements, checkboxes, and comparison table
  return (
    <div data-testid='RenewalConfirmAndEnrollPage' className='max-w-[44rem]'>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-3'>
          Confirm your {planChangeSummary?.next_plan_year?.plan_year} coverage
        </h1>
      </div>
      <form className='flex flex-col gap-y-6' onSubmit={handleSubmit(onSubmit)}>
        {onboardingData && planChangeSummary && (
          <>
            <CoveredParticipantsComparisonTable
              planChangeSummary={planChangeSummary}
              onboardingData={onboardingData}
            />
            <SelectedPlansComparisonTable
              planChangeSummary={planChangeSummary}
              onboardingData={onboardingData}
            />
          </>
        )}

        {(needContractAgreement || needArbitrationAgreement) && (
          <div className='flex flex-col gap-y-2'>
            <h4 className='flex items-center gap-x-2 body-bold text-gray-800 mb-2'>
              <SignatureIcon className='text-gray-500 font-normal' />
              Signatures
            </h4>
            {needArbitrationAgreement && (
              <ArbitrationAgreementCard
                register={register}
                errors={errors}
                isFormSubmitting={isAnyMutationInProgress}
                isEverythingValid={isAgreementValid('arbitration')}
                carrierName={carrierName}
                defaultOpen
              />
            )}
            {needContractAgreement && (
              <ContractAgreementCard
                register={register}
                errors={errors}
                isFormSubmitting={isAnyMutationInProgress}
                isEverythingValid={isAgreementValid('contract')}
                defaultOpen={!needArbitrationAgreement}
              />
            )}
          </div>
        )}
        {needUserPlatformAgreement && (
          <div className='mt-4'>
            <FormCheckboxFieldDS<RenewalConfirmEnrollmentFormData>
              fieldId='user_platform_agreement'
              fieldLabel={
                <>
                  I agree to the{' '}
                  <TextLink
                    href='https://thatch.com/legal/member-agreement'
                    target='_blank'
                    rel='noopener'
                  >
                    Member Agreement
                  </TextLink>
                  ,{' '}
                  <TextLink
                    href='https://thatch.com/legal/financial-partners/consumer-deposit-account-agreement'
                    target='_blank'
                    rel='noopener'
                  >
                    Consumer Deposit Account Agreement
                  </TextLink>
                  , and{' '}
                  <TextLink
                    href='https://thatch.com/legal/financial-partners/debit-cardholder-agreement'
                    target='_blank'
                    rel='noopener'
                  >
                    Debit Cardholder Agreement
                  </TextLink>
                  .
                </>
              }
              error={errors.user_platform_agreement}
              disabled={isAnyMutationInProgress}
              register={register}
              validationRule={{
                ...(needUserPlatformAgreement
                  ? {
                      required: 'You must agree to these agreements to enroll.',
                    }
                  : {}),
              }}
            />
          </div>
        )}
        {showThatchbotOptInCheckbox && (
          <div className='mt-4'>
            <CheckboxGroup label='Thatch is better with Thatchbot (recommended)'>
              <FormCheckboxFieldDS<RenewalConfirmEnrollmentFormData>
                fieldId='member.sms_opt_in'
                fieldLabel='I consent to Thatch sending SMS notifications about my purchases.'
                register={register}
                disabled={isAnyMutationInProgress}
              />
              <p className='text-xs leading-[18px] font-normal text-gray-500 ml-7'>
                Message & data rates may apply. Text STOP to opt out at any
                time.
              </p>
            </CheckboxGroup>
          </div>
        )}
        {submitError && (
          <Banner
            title='A problem has occurred while processing your request'
            type='critical'
          >
            {submitError}
          </Banner>
        )}
        {renderButtons()}
      </form>
    </div>
  )
}
